import React, { useEffect, useState } from "react";
import {
    getBusinessSettingsView,
    getBusinessPricingScopes,
    upsertEnterprisePricingOverride,
    deleteEnterprisePricingOverride,
    upsertOrganizationPricingOverride,
    deleteOrganizationPricingOverride,
    runMasterCatalogJob,
    getCompetitorPriceImportStatus,
    runCompetitorPriceImport,
    updateBusinessSettingsControlPlaneScope,
    updateBusinessSettingsPricingScope,
    getBusinessPricingTopSkusPreview,
} from "../api/businessSettingsApi";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "20px",
    width: "100%",
    maxWidth: "1280px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const embeddedPageStyle = {
    ...pageStyle,
    padding: 0,
};

const emptyValue = "—";
const editableSectionKeysExtended = new Set([
    "master_catalog",
    "orders_biotus",
    "pricing",
    "stock_mapping_mode",
]);
const masterEditableItemKeys = new Set([
    "business_catalog_enabled",
    "business_catalog_interval_seconds",
    "master_weekly_enabled",
    "master_weekly_day",
    "master_weekly_hour",
    "master_weekly_minute",
    "master_daily_publish_enabled",
    "master_daily_publish_hour",
    "master_daily_publish_minute",
    "master_daily_publish_limit",
]);
const biotusEditableItemKeys = new Set([
    "business_order_fetch_enabled",
    "business_order_interval_seconds",
    "business_status_sync_enabled",
    "tabletki_status_4_use_original_order_composition",
    "biotus_enable_unhandled_fallback",
    "biotus_unhandled_order_timeout_minutes",
    "biotus_fallback_additional_status_ids",
    "biotus_duplicate_status_id",
    "biotus_night_start_hour",
    "biotus_night_end_hour",
    "biotus_night_mode",
    "biotus_night_interval_seconds",
]);
const stockEditableItemKeys = new Set([
    "business_stock_enabled",
    "business_stock_interval_seconds",
    "business_runtime_mode",
    "business_stock_mode",
    "publish_priority_tier_enabled",
]);
// Ключи редактируемых полей ценообразования приходят из спеки бэкенда (readonly === false).
const pricingEditableItemKeysFromItems = (items = []) =>
    new Set((items || []).filter((item) => item.readonly === false && String(item.key).startsWith("pricing_")).map((item) => item.key));

const sectionOrder = [
    "master_catalog",
    "stock_mapping_mode",
    "orders_biotus",
    "pricing",
];

const visibleSectionKeys = new Set([
    "master_catalog",
    "stock_mapping_mode",
    "orders_biotus",
    "pricing",
]);

const hiddenItemKeys = new Set([
    "master_target_fallback_note",
    "biotus_enterprise_code",
]);

const decimalDisplayKeys = new Set([
    "pricing_base_thr",
    "pricing_price_band_low_max",
    "pricing_price_band_mid_max",
    "pricing_thr_add_low_uah",
    "pricing_thr_add_mid_uah",
    "pricing_thr_add_high_uah",
    "pricing_no_comp_add_low_uah",
    "pricing_no_comp_add_mid_uah",
    "pricing_no_comp_add_high_uah",
    "pricing_comp_discount_share",
    "pricing_comp_delta_min_uah",
    "pricing_comp_delta_max_uah",
    "pricing_jitter_step_uah",
    "pricing_jitter_min_uah",
    "pricing_jitter_max_uah",
    "pricing_price_band_high_max",
    "pricing_thr_add_premium_uah",
    "pricing_no_comp_add_premium_uah",
    "pricing_top_fix_discount_percent",
]);

const PRICING_DRAFT_DEFAULTS = {
    pricing_base_thr: "0.08",
    pricing_price_band_low_max: "100",
    pricing_price_band_mid_max: "400",
    pricing_price_band_high_max: "800",
    pricing_thr_add_low_uah: "1.0",
    pricing_thr_add_mid_uah: "1.0",
    pricing_thr_add_high_uah: "1.0",
    pricing_thr_add_premium_uah: "1.0",
    pricing_no_comp_add_low_uah: "1.0",
    pricing_no_comp_add_mid_uah: "1.0",
    pricing_no_comp_add_high_uah: "1.0",
    pricing_no_comp_add_premium_uah: "1.0",
    pricing_comp_discount_share: "0.01",
    pricing_comp_delta_min_uah: "2",
    pricing_comp_delta_max_uah: "15",
    pricing_jitter_enabled: false,
    pricing_jitter_step_uah: "0.5",
    pricing_jitter_min_uah: "-1.0",
    pricing_jitter_max_uah: "1.0",
    pricing_top_enabled: false,
    pricing_top_source: "market_top_sales",
    pricing_top_count: "50",
    pricing_top_fix_discount_percent: "0",
};

const PRICING_TOP_SOURCE_LABELS = {
    market_top_sales: "Рынок: топ продаж (загрузка в Закупках)",
    api: "Внешний API (пока не реализован)",
};

// Единый билдер драфта ценообразования: из Map(items) контура и из списка items уровня.
const buildPricingDraft = (itemsMap) => {
    const draft = {};
    Object.entries(PRICING_DRAFT_DEFAULTS).forEach(([key, fallback]) => {
        const raw = itemsMap.get(key);
        const value = raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
        draft[key] = typeof fallback === "boolean" ? Boolean(value ?? fallback) : String(value ?? fallback);
    });
    return draft;
};

const sectionMetaByKey = {
    master_catalog: {
        title: "Мастер-каталог (Master Catalog)",
        description: "Расписание и параметры публикации мастер-каталога.",
    },
    stock_mapping_mode: {
        title: "Business Stock Scheduler",
        description: "Глобальные параметры запуска business stock pipeline.",
    },
    orders_biotus: {
        title: "Дополнительная обработка заказов",
        description: "Fallback-логика и вспомогательные сценарии заказов, не относящиеся к runtime конкретного enterprise.",
    },
    pricing: {
        title: "Ценообразование (Pricing)",
        description: "Глобальные параметры расчета цен.",
    },
};

const innerTabs = [
    { key: "master_catalog", label: "Мастер-каталог" },
    { key: "stock_mapping_mode", label: "Сток и планировщик" },
    { key: "orders_biotus", label: "Заказы" },
    { key: "pricing", label: "Ценообразование — правила" },
    { key: "pricing_scopes", label: "Ценообразование — по уровням" },
];

const getSourceBadge = (source) => {
    if (source === "db") {
        return {
            label: "DB",
            style: {
                backgroundColor: "#ecfeff",
                color: "#155e75",
            },
        };
    }

    if (source === "env" || source === "env-fallback") {
        return {
            label: "ENV",
            style: {
                backgroundColor: "#fff7ed",
                color: "#9a3412",
            },
        };
    }

    if (source === "env_allowlist") {
        return {
            label: "ENV allowlist",
            style: {
                backgroundColor: "#ecfdf5",
                color: "#166534",
            },
        };
    }

    if (source === "default" || source === "computed") {
        return {
            label: "Computed",
            style: {
                backgroundColor: "#f1f5f9",
                color: "#334155",
            },
        };
    }

    if (source === "EnterpriseSettings") {
        return {
            label: "Enterprise",
            style: {
                backgroundColor: "#eef2ff",
                color: "#4338ca",
            },
        };
    }

    return null;
};

const normalizeDecimalDisplay = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw || !/^[-+]?\d+(?:[.,]\d+)?$/.test(raw)) {
        return raw;
    }

    const normalized = raw
        .replace(",", ".")
        .replace(/(\.\d*?[1-9])0+$/, "$1")
        .replace(/\.0+$/, "")
        .replace(".", ",");

    return normalized;
};

const formatValue = (item) => {
    const value = item?.value;
    if (value === null || value === undefined || value === "") {
        return emptyValue;
    }

    if (item?.key === "tabletki_password") {
        return "••••••••";
    }

    if (typeof value === "boolean") {
        return value ? "Да" : "Нет";
    }

    if (decimalDisplayKeys.has(item?.key)) {
        return normalizeDecimalDisplay(value);
    }

    return String(value);
};

const normalizeOptionalValue = (value) => {
    const normalized = String(value || "").trim();
    return normalized || null;
};

const normalizeCommaSeparatedStatusIds = (value) =>
    String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

const parseStatusIdsForPayload = (value) => {
    const parts = normalizeCommaSeparatedStatusIds(value);
    if (parts.length === 0) {
        throw new Error("BIOTUS_FALLBACK_ADDITIONAL_STATUS_IDS должен содержать хотя бы один status id.");
    }

    const parsed = parts.map((item) => Number(item));
    if (parsed.some((item) => !Number.isInteger(item) || item < 1)) {
        throw new Error("BIOTUS_FALLBACK_ADDITIONAL_STATUS_IDS должен содержать только положительные целые status id.");
    }
    return parsed;
};

const parseNonNegativeInteger = (value, label) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) {
        throw new Error(`${label} должен быть неотрицательным целым числом.`);
    }
    return parsed;
};

const parsePositiveInteger = (value, label) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        throw new Error(`${label} должен быть положительным целым числом.`);
    }
    return parsed;
};

const parseHourInteger = (value, label) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 23) {
        throw new Error(`${label} должен быть целым числом от 0 до 23.`);
    }
    return parsed;
};

const parseDecimalString = (value, label, { min = null, exclusiveMin = null, max = null, exclusiveMax = null } = {}) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
        throw new Error(`${label} обязателен.`);
    }

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
        throw new Error(`${label} должен быть числом.`);
    }
    if (min !== null && parsed < min) {
        throw new Error(`${label} должен быть не меньше ${min}.`);
    }
    if (exclusiveMin !== null && parsed <= exclusiveMin) {
        throw new Error(`${label} должен быть больше ${exclusiveMin}.`);
    }
    if (max !== null && parsed > max) {
        throw new Error(`${label} должен быть не больше ${max}.`);
    }
    if (exclusiveMax !== null && parsed >= exclusiveMax) {
        throw new Error(`${label} должен быть меньше ${exclusiveMax}.`);
    }
    return normalized;
};

const flattenItems = (viewModel) => {
    const itemMap = new Map();
    (viewModel?.sections || []).forEach((section) => {
        (section.items || []).forEach((item) => {
            itemMap.set(item.key, item);
        });
    });
    return itemMap;
};

const buildDraftFromViewModel = (viewModel) => {
    const items = flattenItems(viewModel);
    return {
        business_enterprise_code: normalizeOptionalValue(items.get("business_enterprise_code")?.value)
            || normalizeOptionalValue(viewModel?.resolved_enterprise_code)
            || "",
        daily_publish_enterprise_code_override: normalizeOptionalValue(items.get("master_daily_publish_enterprise_explicit")?.value) || "",
        weekly_salesdrive_enterprise_code_override: normalizeOptionalValue(items.get("master_weekly_salesdrive_enterprise_explicit")?.value) || "",
        master_weekly_enabled: Boolean(items.get("master_weekly_enabled")?.value),
        master_weekly_day: normalizeOptionalValue(items.get("master_weekly_day")?.value) || "SUN",
        master_weekly_hour: String(items.get("master_weekly_hour")?.value ?? "3"),
        master_weekly_minute: String(items.get("master_weekly_minute")?.value ?? "0"),
        master_daily_publish_enabled: Boolean(items.get("master_daily_publish_enabled")?.value),
        master_daily_publish_hour: String(items.get("master_daily_publish_hour")?.value ?? "9"),
        master_daily_publish_minute: String(items.get("master_daily_publish_minute")?.value ?? "0"),
        master_daily_publish_limit: String(items.get("master_daily_publish_limit")?.value ?? "0"),
        biotus_enable_unhandled_fallback: Boolean(items.get("biotus_enable_unhandled_fallback")?.value),
        biotus_unhandled_order_timeout_minutes: String(items.get("biotus_unhandled_order_timeout_minutes")?.value ?? "60"),
        biotus_fallback_additional_status_ids: normalizeCommaSeparatedStatusIds(
            items.get("biotus_fallback_additional_status_ids")?.value ?? "9,19,18,20",
        ).join(", "),
        // Только для показа: режим ТТН задаётся в карточке поставщика, в payload не уходит.
        ttn_manual_suppliers: String(items.get("ttn_manual_suppliers")?.value ?? "—"),
        biotus_duplicate_status_id: String(items.get("biotus_duplicate_status_id")?.value ?? "20"),
        biotus_night_start_hour: String(items.get("biotus_night_start_hour")?.value ?? "22"),
        biotus_night_end_hour: String(items.get("biotus_night_end_hour")?.value ?? "8"),
        biotus_night_mode: String(items.get("biotus_night_mode")?.value ?? "skip"),
        biotus_night_interval_seconds: String(items.get("biotus_night_interval_seconds")?.value ?? "60"),
        business_stock_enabled: Boolean(items.get("business_stock_enabled")?.value ?? true),
        business_stock_interval_seconds: String(items.get("business_stock_interval_seconds")?.value ?? "1"),
        business_catalog_enabled: Boolean(items.get("business_catalog_enabled")?.value ?? true),
        business_catalog_interval_seconds: String(items.get("business_catalog_interval_seconds")?.value ?? "1440"),
        business_order_fetch_enabled: Boolean(items.get("business_order_fetch_enabled")?.value ?? false),
        business_order_interval_seconds: String(items.get("business_order_interval_seconds")?.value ?? "1"),
        business_status_sync_enabled: Boolean(items.get("business_status_sync_enabled")?.value ?? true),
        tabletki_status_4_use_original_order_composition: Boolean(items.get("tabletki_status_4_use_original_order_composition")?.value),
        business_runtime_mode: String(items.get("business_runtime_mode")?.value ?? "baseline"),
        business_stock_mode: String(items.get("business_stock_mode")?.value ?? "baseline_legacy"),
        publish_priority_tier_enabled: Boolean(items.get("publish_priority_tier_enabled")?.value ?? false),
        ...buildPricingDraft(items),
    };
};

const buildUpdatePayload = (draft) => ({
    business_enterprise_code: normalizeOptionalValue(draft.business_enterprise_code),
    daily_publish_enterprise_code_override: normalizeOptionalValue(draft.daily_publish_enterprise_code_override),
    weekly_salesdrive_enterprise_code_override: normalizeOptionalValue(draft.weekly_salesdrive_enterprise_code_override),
    business_stock_enabled: Boolean(draft.business_stock_enabled),
    business_stock_interval_seconds: parsePositiveInteger(
        draft.business_stock_interval_seconds,
        "BUSINESS_STOCK_INTERVAL_MINUTES",
    ),
    business_catalog_enabled: Boolean(draft.business_catalog_enabled),
    business_catalog_interval_seconds: parsePositiveInteger(
        draft.business_catalog_interval_seconds,
        "BUSINESS_CATALOG_INTERVAL_MINUTES",
    ),
    business_order_fetch_enabled: Boolean(draft.business_order_fetch_enabled),
    business_order_interval_seconds: parsePositiveInteger(
        draft.business_order_interval_seconds,
        "BUSINESS_ORDER_INTERVAL_MINUTES",
    ),
    business_status_sync_enabled: Boolean(draft.business_status_sync_enabled),
    tabletki_status_4_use_original_order_composition: Boolean(draft.tabletki_status_4_use_original_order_composition),
    business_runtime_mode: draft.business_runtime_mode || null,
    business_stock_mode: draft.business_stock_mode || null,
    publish_priority_tier_enabled: Boolean(draft.publish_priority_tier_enabled),
    biotus_enable_unhandled_fallback: Boolean(draft.biotus_enable_unhandled_fallback),
    biotus_unhandled_order_timeout_minutes: parseNonNegativeInteger(
        draft.biotus_unhandled_order_timeout_minutes,
        "BIOTUS_UNHANDLED_ORDER_TIMEOUT_MINUTES",
    ),
    biotus_fallback_additional_status_ids: parseStatusIdsForPayload(draft.biotus_fallback_additional_status_ids),
    biotus_duplicate_status_id: parsePositiveInteger(
        draft.biotus_duplicate_status_id,
        "BIOTUS_DUPLICATE_STATUS_ID",
    ),
    biotus_night_start_hour: parseHourInteger(
        draft.biotus_night_start_hour,
        "BIOTUS_NIGHT_START_HOUR",
    ),
    biotus_night_end_hour: parseHourInteger(
        draft.biotus_night_end_hour,
        "BIOTUS_NIGHT_END_HOUR",
    ),
    biotus_night_mode: ["skip", "hourly"].includes(draft.biotus_night_mode) ? draft.biotus_night_mode : "skip",
    biotus_night_interval_seconds: parsePositiveInteger(
        draft.biotus_night_interval_seconds,
        "BIOTUS_NIGHT_INTERVAL_MINUTES",
    ) * 60,
    master_weekly_enabled: Boolean(draft.master_weekly_enabled),
    master_weekly_day: String(draft.master_weekly_day || "SUN").toUpperCase(),
    master_weekly_hour: Number(draft.master_weekly_hour),
    master_weekly_minute: Number(draft.master_weekly_minute),
    master_daily_publish_enabled: Boolean(draft.master_daily_publish_enabled),
    master_daily_publish_hour: Number(draft.master_daily_publish_hour),
    master_daily_publish_minute: Number(draft.master_daily_publish_minute),
    master_daily_publish_limit: Number(draft.master_daily_publish_limit),
});

const buildPricingUpdatePayload = (draft) => {
    const payload = {
        pricing_base_thr: parseDecimalString(draft.pricing_base_thr, "Базовый порог", { min: 0 }),
        pricing_price_band_low_max: parseDecimalString(draft.pricing_price_band_low_max, "Верхняя граница LOW", { min: 0 }),
        pricing_price_band_mid_max: parseDecimalString(draft.pricing_price_band_mid_max, "Верхняя граница MID", { min: 0 }),
        pricing_price_band_high_max: parseDecimalString(draft.pricing_price_band_high_max, "Верхняя граница HIGH", { min: 0 }),
        pricing_thr_add_low_uah: parseDecimalString(draft.pricing_thr_add_low_uah, "Надбавка LOW", { min: 0 }),
        pricing_thr_add_mid_uah: parseDecimalString(draft.pricing_thr_add_mid_uah, "Надбавка MID", { min: 0 }),
        pricing_thr_add_high_uah: parseDecimalString(draft.pricing_thr_add_high_uah, "Надбавка HIGH", { min: 0 }),
        pricing_thr_add_premium_uah: parseDecimalString(draft.pricing_thr_add_premium_uah, "Надбавка PREMIUM", { min: 0 }),
        pricing_no_comp_add_low_uah: parseDecimalString(draft.pricing_no_comp_add_low_uah, "Надбавка LOW без конкурента", { min: 0 }),
        pricing_no_comp_add_mid_uah: parseDecimalString(draft.pricing_no_comp_add_mid_uah, "Надбавка MID без конкурента", { min: 0 }),
        pricing_no_comp_add_high_uah: parseDecimalString(draft.pricing_no_comp_add_high_uah, "Надбавка HIGH без конкурента", { min: 0 }),
        pricing_no_comp_add_premium_uah: parseDecimalString(draft.pricing_no_comp_add_premium_uah, "Надбавка PREMIUM без конкурента", { min: 0 }),
        pricing_comp_discount_share: parseDecimalString(draft.pricing_comp_discount_share, "Доля скидки относительно конкурента", { min: 0, exclusiveMax: 1 }),
        pricing_comp_delta_min_uah: parseDecimalString(draft.pricing_comp_delta_min_uah, "Минимальный delta", { min: 0 }),
        pricing_comp_delta_max_uah: parseDecimalString(draft.pricing_comp_delta_max_uah, "Максимальный delta", { min: 0 }),
        pricing_jitter_enabled: Boolean(draft.pricing_jitter_enabled),
        pricing_jitter_step_uah: parseDecimalString(draft.pricing_jitter_step_uah, "Шаг jitter", { exclusiveMin: 0 }),
        pricing_jitter_min_uah: parseDecimalString(draft.pricing_jitter_min_uah, "Минимальный jitter"),
        pricing_jitter_max_uah: parseDecimalString(draft.pricing_jitter_max_uah, "Максимальный jitter"),
        pricing_top_enabled: Boolean(draft.pricing_top_enabled),
        pricing_top_source: Object.keys(PRICING_TOP_SOURCE_LABELS).includes(draft.pricing_top_source) ? draft.pricing_top_source : "market_top_sales",
        pricing_top_count: parsePositiveInteger(draft.pricing_top_count, "Количество топ-товаров"),
        pricing_top_fix_discount_percent: parseDecimalString(draft.pricing_top_fix_discount_percent, "Снижение фикса для топов, %", { min: 0, max: 100 }),
    };

    if (Number(payload.pricing_price_band_mid_max) < Number(payload.pricing_price_band_low_max)) {
        throw new Error("Верхняя граница MID должна быть не меньше верхней границы LOW.");
    }
    if (Number(payload.pricing_price_band_high_max) < Number(payload.pricing_price_band_mid_max)) {
        throw new Error("Верхняя граница HIGH должна быть не меньше верхней границы MID.");
    }
    if (Number(payload.pricing_comp_delta_max_uah) < Number(payload.pricing_comp_delta_min_uah)) {
        throw new Error("Максимальный delta должен быть не меньше минимального delta.");
    }
    if (Number(payload.pricing_jitter_max_uah) < Number(payload.pricing_jitter_min_uah)) {
        throw new Error("Максимальный jitter должен быть не меньше минимального jitter.");
    }

    return payload;
};

const buildPricingDraftFromItems = (items = []) => {
    const map = new Map((items || []).map((item) => [item.key, item.value]));
    return buildPricingDraft(map);
};

const buildItemGroups = (items = []) => {
    const groups = [];
    const map = new Map();

    items.forEach((item) => {
        const groupKey = item.group || "__default__";
        if (!map.has(groupKey)) {
            const entry = {
                key: groupKey,
                title: item.group || "",
                items: [],
            };
            map.set(groupKey, entry);
            groups.push(entry);
        }
        map.get(groupKey).items.push(item);
    });

    return groups;
};

const buildInitialOpenSections = () =>
    sectionOrder.reduce((acc, key) => {
        acc[key] = true;
        return acc;
    }, {});

const sortSections = (sections = []) => {
    const rank = new Map(sectionOrder.map((key, index) => [key, index]));
    return [...sections].sort((left, right) => {
        const leftRank = rank.get(left.key) ?? 999;
        const rightRank = rank.get(right.key) ?? 999;
        return leftRank - rightRank;
    });
};

/* --- themed field primitives for the redesigned «Общие настройки» card (scoped .organization-page) --- */
const OgCheck = ({ label, checked, onChange, disabled = false }) => (
    <button
        type="button"
        className={checked ? "og-check ck" : "og-check"}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
    >
        <span className="box">{checked ? "✓" : ""}</span>
        <span>{label}</span>
    </button>
);

const SectionItem = ({ item }) => {
    const badge = getSourceBadge(item.source);

    return (
        <div className="og-fld">
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                <span>{item.label}</span>
                {badge && (
                    <span className="og-pill" style={badge.style}>
                        {badge.label}
                    </span>
                )}
            </label>
            <div className="val">{formatValue(item)}</div>
            {item.help_text && <div className="help">{item.help_text}</div>}
        </div>
    );
};

const FormField = ({ label, helpText, children, span = false }) => (
    <div className={span ? "og-fld og-col-span" : "og-fld"}>
        <label>{label}</label>
        {children}
        {helpText && <div className="help">{helpText}</div>}
    </div>
);

const EditToolbar = ({ editable, saving, onCancel, onSave }) => {
    if (!editable) {
        return null;
    }

    return (
        <div className="og-save-bar notop">
            <button type="button" className="og-btn og-btn-primary" onClick={onSave} disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
            </button>
            <button type="button" className="og-btn og-btn-ghost" onClick={onCancel} disabled={saving}>
                Отмена
            </button>
        </div>
    );
};

const TargetEnterpriseEditor = ({ draft, onChange }) => (
    <div className="og-block">
        <p className="og-mini">
            Управление enterprise runtime перенесено на страницу Business-магазинов. Здесь остаётся только control-plane summary.
        </p>
    </div>
);

const MasterCatalogEditor = ({ draft, onChange, manualRunState, onManualRun }) => (
    <div>
        <div className="og-block">
            <div className="og-bh">
                <span className="ic">▤</span>
                <h4>Публикация Business-каталога</h4>
            </div>
            <div className="og-grid2">
                <OgCheck
                    label="Включено"
                    checked={draft.business_catalog_enabled}
                    onChange={(value) => onChange("business_catalog_enabled", value)}
                />
                <FormField label="Интервал Business catalog, минут">
                    <input
                        type="number"
                        min="1"
                        className="og-inp"
                        value={draft.business_catalog_interval_seconds}
                        onChange={(event) => onChange("business_catalog_interval_seconds", event.target.value)}
                    />
                </FormField>
            </div>
            <p className="og-mini" style={{ marginTop: "8px" }}>Business catalog отправляется напрямую и не записывает inventory_data.</p>
            <p className="og-mini" style={{ marginTop: "4px" }}>Ручной publish остается доступен через Business catalog flow.</p>
        </div>

        <div className="og-block">
            <div className="og-bh">
                <span className="ic">📅</span>
                <h4>Еженедельное обновление</h4>
                <span className="hint">weekly_enrichment</span>
            </div>
            <div className="og-grid4">
                <OgCheck
                    label="Включено"
                    checked={draft.master_weekly_enabled}
                    onChange={(value) => onChange("master_weekly_enabled", value)}
                />
                <FormField label="День">
                    <select
                        className="og-inp"
                        value={draft.master_weekly_day}
                        onChange={(event) => onChange("master_weekly_day", event.target.value)}
                    >
                        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
                            <option key={day} value={day}>
                                {day}
                            </option>
                        ))}
                    </select>
                </FormField>
                <FormField label="Час">
                    <input
                        type="number"
                        min="0"
                        max="23"
                        className="og-inp"
                        value={draft.master_weekly_hour}
                        onChange={(event) => onChange("master_weekly_hour", event.target.value)}
                    />
                </FormField>
                <FormField label="Минута">
                    <input
                        type="number"
                        min="0"
                        max="59"
                        className="og-inp"
                        value={draft.master_weekly_minute}
                        onChange={(event) => onChange("master_weekly_minute", event.target.value)}
                    />
                </FormField>
            </div>
            <div className="og-save-bar notop">
                <button
                    type="button"
                    className="og-btn og-btn-ghost og-btn-sm"
                    onClick={() => onManualRun("weekly_enrichment")}
                    disabled={Boolean(manualRunState?.job)}
                >
                    {manualRunState?.job === "weekly_enrichment" ? "Запуск..." : "▶ Запустить недельное обновление"}
                </button>
                {manualRunState?.message && manualRunState?.jobType === "weekly_enrichment" && (
                    <span className={manualRunState.type === "error" ? "og-msg err" : "og-msg ok"}>
                        {manualRunState.message}
                    </span>
                )}
            </div>
        </div>

        <div className="og-block">
            <div className="og-bh">
                <span className="ic">📤</span>
                <h4>Ежедневная выгрузка</h4>
            </div>
            <div className="og-grid4">
                <OgCheck
                    label="Включено"
                    checked={draft.master_daily_publish_enabled}
                    onChange={(value) => onChange("master_daily_publish_enabled", value)}
                />
                <FormField label="Час">
                    <input
                        type="number"
                        min="0"
                        max="23"
                        className="og-inp"
                        value={draft.master_daily_publish_hour}
                        onChange={(event) => onChange("master_daily_publish_hour", event.target.value)}
                    />
                </FormField>
                <FormField label="Минута">
                    <input
                        type="number"
                        min="0"
                        max="59"
                        className="og-inp"
                        value={draft.master_daily_publish_minute}
                        onChange={(event) => onChange("master_daily_publish_minute", event.target.value)}
                    />
                </FormField>
                <FormField label="Лимит публикации">
                    <input
                        type="number"
                        min="0"
                        className="og-inp"
                        value={draft.master_daily_publish_limit}
                        onChange={(event) => onChange("master_daily_publish_limit", event.target.value)}
                    />
                </FormField>
            </div>
            <div className="og-save-bar notop">
                <button
                    type="button"
                    className="og-btn og-btn-ghost og-btn-sm"
                    onClick={() => onManualRun("daily_publish")}
                    disabled={Boolean(manualRunState?.job)}
                >
                    {manualRunState?.job === "daily_publish" ? "Запуск..." : "▶ Запустить дневную выгрузку"}
                </button>
                {manualRunState?.message && manualRunState?.jobType === "daily_publish" && (
                    <span className={manualRunState.type === "error" ? "og-msg err" : "og-msg ok"}>
                        {manualRunState.message}
                    </span>
                )}
            </div>
        </div>
    </div>
);

const AdditionalOrdersProcessingEditor = ({ draft, onChange }) => (
    <div className="og-block">
        <div className="og-bh">
            <span className="ic">↺</span>
            <h4>Дополнительная обработка заказов</h4>
        </div>
        <p className="og-mini" style={{ margin: "0 0 12px" }}>
            Настройки обработки заказов, которые не были обработаны основным контуром
        </p>
        <div className="og-grid2">
            <OgCheck
                label="Включить дополнительную обработку"
                checked={draft.biotus_enable_unhandled_fallback}
                onChange={(value) => onChange("biotus_enable_unhandled_fallback", value)}
            />
            <FormField label="Ожидание, минут">
                <input
                    type="number"
                    min="0"
                    className="og-inp"
                    value={draft.biotus_unhandled_order_timeout_minutes}
                    onChange={(event) => onChange("biotus_unhandled_order_timeout_minutes", event.target.value)}
                />
            </FormField>
            <FormField label="Статус для дублей">
                <input
                    type="number"
                    min="1"
                    className="og-inp"
                    value={draft.biotus_duplicate_status_id}
                    onChange={(event) => onChange("biotus_duplicate_status_id", event.target.value)}
                />
            </FormField>
            <FormField
                span
                label="Дополнительные статусы SalesDrive"
                helpText="Введите status id через запятую. Используются только положительные целые значения."
            >
                <input
                    type="text"
                    className="og-inp"
                    value={draft.biotus_fallback_additional_status_ids}
                    onChange={(event) => onChange("biotus_fallback_additional_status_ids", event.target.value)}
                    placeholder="9, 19, 18, 20"
                />
            </FormField>
            <FormField
                span
                label="Отправители «ТТН добавляем позже»"
                helpText="Настраивается в карточке поставщика: Поставщики → Заказы и график → ТТН. На площадке можно переопределить в таблице отправителей."
            >
                <div className="og-inp" aria-readonly="true">
                    {draft.ttn_manual_suppliers || "—"}
                </div>
            </FormField>
        </div>
    </div>
);

const NightOrdersProcessingEditor = ({ draft, onChange }) => (
    <div className="og-block">
        <div className="og-bh">
            <span className="ic">🌙</span>
            <h4>Ночной режим обработки заказов</h4>
        </div>
        <p className="og-mini" style={{ margin: "0 0 12px" }}>
            Период, когда Business order processor пропускает обработку или запускается реже
        </p>
        <div className="og-grid4">
            <FormField label="Начало ночного окна, час">
                <input
                    type="number"
                    min="0"
                    max="23"
                    className="og-inp"
                    value={draft.biotus_night_start_hour}
                    onChange={(event) => onChange("biotus_night_start_hour", event.target.value)}
                />
            </FormField>
            <FormField label="Конец ночного окна, час">
                <input
                    type="number"
                    min="0"
                    max="23"
                    className="og-inp"
                    value={draft.biotus_night_end_hour}
                    onChange={(event) => onChange("biotus_night_end_hour", event.target.value)}
                />
            </FormField>
            <FormField label="Режим ночной обработки">
                <select
                    className="og-inp"
                    value={draft.biotus_night_mode}
                    onChange={(event) => onChange("biotus_night_mode", event.target.value)}
                >
                    <option value="skip">skip</option>
                    <option value="hourly">hourly</option>
                </select>
            </FormField>
            <FormField label="Ночной интервал, минут">
                <input
                    type="number"
                    min="1"
                    className="og-inp"
                    value={draft.biotus_night_interval_seconds}
                    onChange={(event) => onChange("biotus_night_interval_seconds", event.target.value)}
                />
            </FormField>
        </div>
    </div>
);

const StockOperationalEditor = ({ draft, onChange }) => (
    <div className="og-block">
        <div className="og-bh">
            <span className="ic">⚙</span>
            <h4>Business Stock Scheduler</h4>
        </div>
        <div className="og-grid2">
            <OgCheck
                label="Включить обработку стока"
                checked={draft.business_stock_enabled}
                onChange={(value) => onChange("business_stock_enabled", value)}
            />
            <FormField label="Интервал запуска, минут" helpText="Через сколько минут запускается следующий цикл обработки остатков.">
                <input
                    type="number"
                    min="1"
                    className="og-inp"
                    value={draft.business_stock_interval_seconds}
                    onChange={(event) => onChange("business_stock_interval_seconds", event.target.value)}
                />
            </FormField>
            <FormField label="Business runtime mode">
                <select
                    className="og-inp"
                    value={draft.business_runtime_mode}
                    onChange={(event) => onChange("business_runtime_mode", event.target.value)}
                >
                    <option value="baseline">baseline</option>
                    <option value="custom">custom</option>
                </select>
            </FormField>
            <FormField label="Business stock mode">
                <select
                    className="og-inp"
                    value={draft.business_stock_mode}
                    onChange={(event) => onChange("business_stock_mode", event.target.value)}
                >
                    <option value="baseline_legacy">baseline_legacy</option>
                    <option value="store_aware">store_aware</option>
                </select>
            </FormField>
            <OgCheck
                label="Ярус приоритета отправителей"
                checked={draft.publish_priority_tier_enabled}
                onChange={(value) => onChange("publish_priority_tier_enabled", value)}
            />
        </div>
        <p className="og-mini" style={{ marginTop: "8px" }}>Включает или выключает обработку остатков.</p>
        <p className="og-mini" style={{ marginTop: "4px" }}>
            Ярус приоритета: отправитель с большим ярусом побеждает раньше сравнения цены во всех каналах
            (Tabletki, Prom, Rozetka, Kasta, выбор поставщика в заказе). Выключено — прежний порядок «цена, потом приоритет».
        </p>
    </div>
);

const OrdersSectionEditor = ({ draft, onChange }) => (
    <div>
        <div className="og-block">
            <div className="og-bh">
                <span className="ic">📦</span>
                <h4>Получение и синхронизация заказов</h4>
            </div>
            <div className="og-grid3">
                <OgCheck
                    label="Получение заказов Business"
                    checked={draft.business_order_fetch_enabled}
                    onChange={(value) => onChange("business_order_fetch_enabled", value)}
                />
                <FormField label="Интервал забора заказов, минут">
                    <input
                        type="number"
                        min="1"
                        className="og-inp"
                        value={draft.business_order_interval_seconds}
                        onChange={(event) => onChange("business_order_interval_seconds", event.target.value)}
                    />
                </FormField>
                <OgCheck
                    label="Синхронизация статусов Business"
                    checked={draft.business_status_sync_enabled}
                    onChange={(value) => onChange("business_status_sync_enabled", value)}
                />
                <OgCheck
                    label="Статус 4: первоначальный состав заказа"
                    checked={draft.tabletki_status_4_use_original_order_composition}
                    onChange={(value) => onChange("tabletki_status_4_use_original_order_composition", value)}
                />
            </div>
            <p className="og-mini" style={{ marginTop: "8px" }}>При включении подтверждение в Tabletki отправляется с первоначальными товарами, количеством и ценами, даже если состав был изменён в SalesDrive.</p>
        </div>
        <NightOrdersProcessingEditor draft={draft} onChange={onChange} />
        <AdditionalOrdersProcessingEditor draft={draft} onChange={onChange} />
    </div>
);

const formatDateTime = (value) => {
    if (!value) return emptyValue;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("uk-UA", { dateStyle: "medium", timeStyle: "medium" }).format(date);
};

const CompetitorPricesCard = ({ status, runState, onRun }) => {
    const source = status?.source || {};
    const sourceMode = status?.source_mode || "drive";
    const apiSnapshot = status?.api_last_snapshot;
    const usesApi = sourceMode === "api";
    const lastRun = status?.last_run;
    const lastSuccess = status?.last_success;
    const running = Boolean(!usesApi && (runState?.running || status?.is_running || lastRun?.status === "running"));
    const statusText = !status
        ? "Загрузка статуса..."
        : usesApi
            ? apiSnapshot?.status === "succeeded"
                ? "Последний API-снимок принят"
                : apiSnapshot?.status === "rejected"
                    ? "Последний API-снимок отклонён"
                    : "API-снимки ещё не поступали"
        : running
            ? "Импорт выполняется"
            : lastRun?.status === "succeeded"
                ? "Последний импорт завершён успешно"
                : lastRun?.status === "skipped"
                    ? "Файл не изменился — импорт пропущен"
                    : lastRun?.status === "failed"
                        ? "Последний импорт завершился ошибкой"
                        : "Импорт ещё не запускался";

    return (
        <div className="og-block">
            <div className="og-bh">
                <span className="ic">⚔</span>
                <h4>Цены конкурентов</h4>
            </div>
            <div className="og-grid3">
                <div className="og-fld"><label>{usesApi ? "Источник" : "Файл на Google Drive"}</label><div className="val">{usesApi ? "Внешний API" : source.file_name || emptyValue}</div><div className="help">{usesApi ? `Снимок: ${apiSnapshot?.snapshot_id || emptyValue}` : `Изменён: ${formatDateTime(source.modified_at)}`}</div></div>
                <div className="og-fld"><label>Последняя загрузка в БД</label><div className="val">{formatDateTime(usesApi ? apiSnapshot?.activated_at : lastSuccess?.finished_at)}</div><div className="help">Строк: {usesApi ? apiSnapshot?.price_count ?? 0 : lastSuccess?.imported_rows ?? 0}{usesApi ? "" : `, городов: ${lastSuccess?.imported_cities ?? 0}`}</div></div>
                <div className="og-fld"><label>Статус</label><div className="val">{statusText}</div><div className="help">БД обновлена: {formatDateTime(status?.database_updated_at)}</div></div>
            </div>
            {source.error && <p className="og-msg err">Не удалось проверить файл на Drive: {source.error}</p>}
            {!usesApi && lastRun?.status === "failed" && lastRun.error_message && <p className="og-msg err">Ошибка импорта: {lastRun.error_message}</p>}
            {usesApi && apiSnapshot?.status === "rejected" && apiSnapshot?.error_message && <p className="og-msg err">API-снимок отклонён: {apiSnapshot.error_message}</p>}
            {usesApi && apiSnapshot?.status === "succeeded" && <p className="og-mini">Проверка пройдена. Сравнение с предыдущим снимком: обновлено цен — {apiSnapshot.changed_price_count ?? 0}, добавлено — {apiSnapshot.added_price_count ?? 0}, удалено — {apiSnapshot.removed_price_count ?? 0}, без изменений — {apiSnapshot.unchanged_price_count ?? 0}.</p>}
            {runState?.message && <p className={runState.type === "error" ? "og-msg err" : "og-msg ok"}>{runState.message}</p>}
            {!usesApi && <div className="og-save-bar notop">
                <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={onRun} disabled={running}>
                    {running ? "Обновление..." : "↻ Обновить цены конкурентов"}
                </button>
            </div>}
            <p className="og-mini" style={{ marginTop: "8px" }}>{usesApi ? "Цены автоматически передаёт внешний сервис. Новые цены будут учтены при ближайшем штатном пересчёте офферов." : "Новые цены будут учтены при ближайшем штатном пересчёте офферов."}</p>
        </div>
    );
};

const TopSkusPreview = ({ scopeType = "business", scopeRef = null }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            setData(await getBusinessPricingTopSkusPreview(scopeType, scopeRef));
        } catch (loadError) {
            setError("Не удалось загрузить топ-список.");
        } finally {
            setLoading(false);
        }
    };

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !data && !loading) load();
    };

    const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("ru-RU") : "—");

    return (
        <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={toggle}>
                    {open ? "Скрыть текущий топ-список" : "Показать текущий топ-список"}
                </button>
                {open && data && (
                    <span className={data.enabled ? "og-badge on" : "og-badge off"}>
                        {data.enabled && <span className="dot" />}
                        {data.enabled ? `Включено: топ-${data.count}, −${data.fix_discount_percent}%` : "Выключено"}
                    </span>
                )}
                {open && data && data.items.length > 0 && (
                    <span className="og-mini">
                        В наличии сейчас: {data.offered_count} из {data.items.length}
                    </span>
                )}
                {open && !loading && (
                    <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={load}>Обновить</button>
                )}
            </div>
            {open && (
                <div style={{ marginTop: "10px" }}>
                    <p className="og-mini">Список для сохранённых настроек этого уровня (действующий: {data?.effective_scope_type || "…"}{data?.effective_scope_ref ? ` / ${data.effective_scope_ref}` : ""}). Источник — последняя проанализированная загрузка «Топ продаж»; новая загрузка меняет список при следующем пересчёте офферов.</p>
                    {loading && <p className="og-mini">Загрузка…</p>}
                    {error && <p className="og-mini" style={{ color: "var(--og-danger, #c0392b)" }}>{error}</p>}
                    {data?.warning && <p className="og-mini" style={{ color: "var(--og-warn, #b7791f)" }}>{data.warning}</p>}
                    {data && (
                        <p className="og-mini">
                            Загрузка: {data.upload_filename || "—"} (id {data.upload_id ?? "—"}), период {fmtDate(data.upload_date_from)} – {fmtDate(data.upload_date_to)}, проанализирована {data.upload_analyzed_at ? new Date(data.upload_analyzed_at).toLocaleString("ru-RU") : "—"}.
                        </p>
                    )}
                    {data && data.items.length > 0 && (
                        <div style={{ maxHeight: "320px", overflow: "auto", marginTop: "8px" }}>
                            <table className="og-table" style={{ width: "100%", fontSize: "12px" }}>
                                <thead>
                                    <tr><th>#</th><th>SKU</th><th>Название</th><th style={{ textAlign: "right" }}>Продажи, шт</th><th style={{ textAlign: "right" }}>Продажи, грн</th><th>Оффер</th></tr>
                                </thead>
                                <tbody>
                                    {data.items.map((item) => (
                                        <tr key={item.sku}>
                                            <td>{item.rank}</td>
                                            <td>{item.sku}</td>
                                            <td>{item.name || "—"}</td>
                                            <td style={{ textAlign: "right" }}>{Number(item.sales_qty).toLocaleString("ru-RU")}</td>
                                            <td style={{ textAlign: "right" }}>{Number(item.sales_amount).toLocaleString("ru-RU", { maximumFractionDigits: 0 })}</td>
                                            <td>{item.has_offer ? "есть" : "нет"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PricingEditor = ({ draft, onChange, competitorImportStatus, competitorImportState, onCompetitorImport, scopeType = "business", scopeRef = null }) => {
    const renderDecimalInput = (key, label, helpText, extra = {}) => (
        <FormField label={label} helpText={helpText}>
            <input
                type="number"
                step={extra.step || "0.01"}
                min={extra.min}
                className="og-inp"
                value={draft[key]}
                onChange={(event) => onChange(key, event.target.value)}
            />
        </FormField>
    );

    return (
        <div>
            {onCompetitorImport && (
                <CompetitorPricesCard
                    status={competitorImportStatus}
                    runState={competitorImportState}
                    onRun={onCompetitorImport}
                />
            )}
            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">₴</span>
                    <h4>Базовый порог и диапазоны цен</h4>
                </div>
                <div className="og-grid3">
                    {renderDecimalInput("pricing_base_thr", "Базовый порог", "Доля, которая участвует в threshold calculation. 0.08 = 8%.", { step: "0.000001", min: "0" })}
                    {renderDecimalInput("pricing_price_band_low_max", "Верхняя граница LOW", "Если price_opt меньше или равен этому значению, товар попадает в LOW.", { min: "0" })}
                    {renderDecimalInput("pricing_price_band_mid_max", "Верхняя граница MID", "Если price_opt выше LOW, но не выше этого значения, товар попадает в MID.", { min: "0" })}
                    {renderDecimalInput("pricing_price_band_high_max", "Верхняя граница HIGH", "Если price_opt выше MID, но не выше этого значения, товар попадает в HIGH. Всё, что выше, идёт в PREMIUM.", { min: "0" })}
                </div>
            </div>

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">⚔</span>
                    <h4>Реакция на конкурентов</h4>
                </div>
                <div className="og-grid3">
                    {renderDecimalInput("pricing_thr_add_low_uah", "Надбавка LOW, грн", "Абсолютная надбавка в гривне для LOW, когда есть конкурент.", { min: "0" })}
                    {renderDecimalInput("pricing_thr_add_mid_uah", "Надбавка MID, грн", "Абсолютная надбавка в гривне для MID, когда есть конкурент.", { min: "0" })}
                    {renderDecimalInput("pricing_thr_add_high_uah", "Надбавка HIGH, грн", "Абсолютная надбавка в гривне для HIGH, когда есть конкурент.", { min: "0" })}
                    {renderDecimalInput("pricing_thr_add_premium_uah", "Надбавка PREMIUM, грн", "Абсолютная надбавка в гривне для PREMIUM (выше границы HIGH), когда есть конкурент.", { min: "0" })}
                    {renderDecimalInput("pricing_comp_discount_share", "Доля скидки относительно конкурента", "Share, а не проценты: 0.01 = 1%. Используется для undercut логики.", { step: "0.000001", min: "0" })}
                    {renderDecimalInput("pricing_comp_delta_min_uah", "Минимальный delta, грн", "Минимальный допустимый отступ от цены конкурента в гривне.", { min: "0" })}
                    {renderDecimalInput("pricing_comp_delta_max_uah", "Максимальный delta, грн", "Максимальный допустимый отступ от цены конкурента в гривне.", { min: "0" })}
                </div>
            </div>

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">∅</span>
                    <h4>Поведение без конкурентов</h4>
                </div>
                <div className="og-grid3">
                    {renderDecimalInput("pricing_no_comp_add_low_uah", "Надбавка LOW без конкурента, грн", "Абсолютная надбавка в гривне для LOW, когда цены конкурента нет.", { min: "0" })}
                    {renderDecimalInput("pricing_no_comp_add_mid_uah", "Надбавка MID без конкурента, грн", "Абсолютная надбавка в гривне для MID, когда цены конкурента нет.", { min: "0" })}
                    {renderDecimalInput("pricing_no_comp_add_high_uah", "Надбавка HIGH без конкурента, грн", "Абсолютная надбавка в гривне для HIGH, когда цены конкурента нет.", { min: "0" })}
                    {renderDecimalInput("pricing_no_comp_add_premium_uah", "Надбавка PREMIUM без конкурента, грн", "Абсолютная надбавка в гривне для PREMIUM, когда цены конкурента нет.", { min: "0" })}
                </div>
            </div>

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">★</span>
                    <h4>Топ-товары</h4>
                </div>
                <OgCheck
                    label="Отдельная надбавка для топ-товаров"
                    checked={draft.pricing_top_enabled}
                    onChange={(value) => onChange("pricing_top_enabled", value)}
                />
                <p className="og-mini" style={{ margin: "8px 0 12px" }}>
                    Для товаров из топ-списка порог = закупка × (1 + базовый порог) + (надбавка диапазона + фикс поставщика) × (1 − % / 100). На РРЦ и dumping не влияет.
                </p>
                <div className="og-grid3">
                    <FormField label="Источник топ-списка" helpText="Пока доступна только загрузка «Топ продаж» из раздела Закупки. Внешний API зарезервирован.">
                        <select
                            className="og-inp"
                            value={draft.pricing_top_source}
                            onChange={(event) => onChange("pricing_top_source", event.target.value)}
                            disabled
                        >
                            {Object.entries(PRICING_TOP_SOURCE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </FormField>
                    {renderDecimalInput("pricing_top_count", "Количество топ-товаров", "Сколько первых SKU по продажам считаются топом, например 50.", { step: "1", min: "1" })}
                    {renderDecimalInput("pricing_top_fix_discount_percent", "Снижение фикса для топов, %", "Процент от суммы (надбавка диапазона + фикс поставщика). 0 = без изменений.", { step: "0.01", min: "0" })}
                </div>
                <TopSkusPreview scopeType={scopeType} scopeRef={scopeRef} />
            </div>

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">≈</span>
                    <h4>Jitter</h4>
                </div>
                <OgCheck
                    label="Включить jitter"
                    checked={draft.pricing_jitter_enabled}
                    onChange={(value) => onChange("pricing_jitter_enabled", value)}
                />
                <p className="og-mini" style={{ margin: "8px 0 12px" }}>
                    Добавляет случайное смещение после основного расчёта цены. Runtime формулы при этом не меняются.
                </p>
                <div className="og-grid3">
                    {renderDecimalInput("pricing_jitter_step_uah", "Шаг jitter, грн", "Шаг сетки, по которой выбирается случайное смещение цены.", { min: "0.01" })}
                    {renderDecimalInput("pricing_jitter_min_uah", "Минимальный jitter, грн", "Нижняя граница случайного смещения цены.", {})}
                    {renderDecimalInput("pricing_jitter_max_uah", "Максимальный jitter, грн", "Верхняя граница случайного смещения цены.", {})}
                </div>
            </div>
        </div>
    );
};

const SectionCard = ({
    section,
    isOpen,
    saving,
    onToggle,
    onCancel,
    onSave,
    draft,
    onDraftChange,
    manualRunState,
    onManualRun,
    competitorImportStatus,
    competitorImportState,
    onCompetitorImport,
}) => {
    const sectionMeta = sectionMetaByKey[section.key] || {};
    const groups = buildItemGroups(section.items.filter((item) => !hiddenItemKeys.has(item.key)));
    const editable = editableSectionKeysExtended.has(section.key);
    const editableKeys = section.key === "master_catalog"
            ? masterEditableItemKeys
            : section.key === "orders_biotus"
                ? biotusEditableItemKeys
                : section.key === "stock_mapping_mode"
                    ? stockEditableItemKeys
                    : section.key === "pricing"
                        ? pricingEditableItemKeysFromItems(section.items)
            : new Set();
    const visibleGroups = isOpen && editable
        ? groups
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => !editableKeys.has(item.key)),
            }))
            .filter((group) => group.items.length > 0)
        : groups;

    return (
        <div className="og-card og-toolbar">
            <div className="th">
                <div>
                    <h2>{sectionMeta.title || section.title}</h2>
                    {(sectionMeta.description || section.description) && (
                        <p>{sectionMeta.description || section.description}</p>
                    )}
                </div>
                <div className="acts">
                    <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => onToggle(section.key)} disabled={saving}>
                        {isOpen ? "Свернуть" : "Развернуть"}
                    </button>
                </div>
            </div>
            {isOpen && draft && (
                <div style={{ display: "grid", gap: "18px", marginTop: "16px" }}>
                    <EditToolbar
                        editable={editable}
                        saving={saving}
                        onCancel={onCancel}
                        onSave={() => onSave(section.key)}
                    />
                    {section.key === "target_enterprise" && (
                        <TargetEnterpriseEditor
                            draft={draft}
                            onChange={onDraftChange}
                        />
                    )}
                    {section.key === "master_catalog" && (
                        <MasterCatalogEditor
                            draft={draft}
                            onChange={onDraftChange}
                            manualRunState={manualRunState}
                            onManualRun={onManualRun}
                        />
                    )}
                    {section.key === "orders_biotus" && (
                        <OrdersSectionEditor
                            draft={draft}
                            onChange={onDraftChange}
                        />
                    )}
                    {section.key === "stock_mapping_mode" && (
                        <StockOperationalEditor
                            draft={draft}
                            onChange={onDraftChange}
                        />
                    )}
                    {section.key === "pricing" && (
                        <PricingEditor
                            draft={draft}
                            onChange={onDraftChange}
                            competitorImportStatus={competitorImportStatus}
                            competitorImportState={competitorImportState}
                            onCompetitorImport={onCompetitorImport}
                        />
                    )}
                    <div style={{ display: "grid", gap: "18px" }}>
                        {visibleGroups.map((group) => (
                            <div key={group.key} className="og-block">
                                {group.title && (
                                    <div className="og-bh" style={{ margin: "0 0 10px" }}>
                                        <h4 style={{ fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--og-muted)" }}>
                                            {group.title}
                                        </h4>
                                    </div>
                                )}
                                <div className="og-grid3">
                                    {group.items.map((item) => (
                                        <SectionItem key={item.key} item={item} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const PricingScopeRow = ({ scope, isOpen, saving, draft, onToggle, onDraftChange, onSave, onReset }) => {
    const scopeKey = `${scope.scope_type}:${scope.scope_ref}`;
    return (
        <div className={isOpen ? "og-scope-row open" : "og-scope-row"}>
            <div className="og-sr-head">
                <span className="nm">{scope.scope_name}</span>
                <span className="ref">{scope.scope_ref}</span>
                <div className="sp">
                    <span className={scope.has_override ? "og-badge on" : "og-badge off"}>
                        {scope.has_override && <span className="dot" />}
                        {scope.has_override ? "Своя настройка" : "Наследует контур"}
                    </span>
                    <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => onToggle(scopeKey)} disabled={saving}>
                        {isOpen ? "Свернуть" : (scope.has_override ? "Изменить" : "Задать свои")}
                    </button>
                </div>
            </div>
            {isOpen && (
                <div className="og-sr-body">
                    <div className="og-save-bar notop">
                        <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={() => onSave(scope)} disabled={saving}>
                            {saving ? "Сохранение..." : "Сохранить"}
                        </button>
                        {scope.has_override && (
                            <button type="button" className="og-btn og-btn-soft-danger og-btn-sm" onClick={() => onReset(scope)} disabled={saving}>
                                Сбросить к контуру
                            </button>
                        )}
                    </div>
                    <PricingEditor draft={draft} onChange={onDraftChange} scopeType={scope.scope_type} scopeRef={scope.scope_ref} />
                </div>
            )}
        </div>
    );
};

const PricingScopesCard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [openKey, setOpenKey] = useState("");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const result = await getBusinessPricingScopes();
                setData(result);
            } catch (loadError) {
                console.error("Error loading pricing scopes:", loadError);
                setError("Не удалось загрузить ценообразование по уровням.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const replaceScope = (updatedScope) => {
        setData((current) => {
            if (!current) {
                return current;
            }
            const listKey = updatedScope.scope_type === "enterprise" ? "enterprises" : "organizations";
            const next = { ...current };
            next[listKey] = (current[listKey] || []).map((scope) =>
                scope.scope_ref === updatedScope.scope_ref ? updatedScope : scope,
            );
            return next;
        });
    };

    const handleToggleScope = (scopeKey) => {
        setMessage({ type: "", text: "" });
        if (openKey === scopeKey) {
            setOpenKey("");
            setDraft(null);
            return;
        }
        const [scopeType, ...refParts] = scopeKey.split(":");
        const scopeRef = refParts.join(":");
        const list = scopeType === "enterprise" ? data?.enterprises : data?.organizations;
        const scope = (list || []).find((item) => item.scope_ref === scopeRef);
        setOpenKey(scopeKey);
        setDraft(buildPricingDraftFromItems(scope?.items));
    };

    const handleDraftChange = (key, value) => {
        setDraft((current) => ({ ...(current || {}), [key]: value }));
    };

    const handleSave = async (scope) => {
        setSaving(true);
        setMessage({ type: "", text: "" });
        try {
            const payload = buildPricingUpdatePayload(draft);
            const updated = scope.scope_type === "enterprise"
                ? await upsertEnterprisePricingOverride(scope.scope_ref, payload)
                : await upsertOrganizationPricingOverride(scope.scope_ref, payload);
            replaceScope(updated);
            setMessage({ type: "success", text: `Сохранено: ${updated.scope_name}.` });
        } catch (saveError) {
            console.error("Error saving pricing override:", saveError);
            setMessage({
                type: "error",
                text: saveError?.response?.data?.detail || saveError?.message || "Не удалось сохранить.",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async (scope) => {
        setSaving(true);
        setMessage({ type: "", text: "" });
        try {
            const updated = scope.scope_type === "enterprise"
                ? await deleteEnterprisePricingOverride(scope.scope_ref)
                : await deleteOrganizationPricingOverride(scope.scope_ref);
            replaceScope(updated);
            setDraft(buildPricingDraftFromItems(updated.items));
            setMessage({ type: "success", text: `Сброшено к контуру: ${updated.scope_name}.` });
        } catch (resetError) {
            console.error("Error resetting pricing override:", resetError);
            setMessage({
                type: "error",
                text: resetError?.response?.data?.detail || resetError?.message || "Не удалось сбросить.",
            });
        } finally {
            setSaving(false);
        }
    };

    const renderScopeList = (title, scopes) => (
        <div style={{ marginBottom: "18px" }}>
            <div className="og-bh" style={{ margin: "0 0 10px" }}>
                <h4 style={{ fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--og-muted)" }}>{title}</h4>
            </div>
            {(scopes || []).length === 0 ? (
                <p className="og-mini">Нет доступных записей.</p>
            ) : (
                (scopes || []).map((scope) => {
                    const scopeKey = `${scope.scope_type}:${scope.scope_ref}`;
                    return (
                        <PricingScopeRow
                            key={scopeKey}
                            scope={scope}
                            isOpen={openKey === scopeKey}
                            saving={saving && openKey === scopeKey}
                            draft={openKey === scopeKey ? draft : null}
                            onToggle={handleToggleScope}
                            onDraftChange={handleDraftChange}
                            onSave={handleSave}
                            onReset={handleReset}
                        />
                    );
                })
            )}
        </div>
    );

    return (
        <div className="og-card og-toolbar">
            <div className="th">
                <div>
                    <h2>Ценообразование по уровням</h2>
                    <p>
                        Переопределение параметров для предприятий и организаций. Приоритет: предприятие → организация → контур.
                        Уровень либо наследует контур целиком, либо задаёт весь набор значений.
                    </p>
                </div>
                <div className="acts">
                    <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => setExpanded((value) => !value)}>
                        {expanded ? "Свернуть" : "Развернуть"}
                    </button>
                </div>
            </div>
            {expanded && (
                <div style={{ display: "grid", gap: "18px", marginTop: "16px" }}>
                    {message.text && (
                        <p className={message.type === "error" ? "og-msg err" : "og-msg ok"}>{message.text}</p>
                    )}
                    {loading && <p className="og-mini">Загрузка...</p>}
                    {!loading && error && <p className="og-msg err">{error}</p>}
                    {!loading && !error && data && (
                        <>
                            {renderScopeList("Предприятия", data.enterprises)}
                            {renderScopeList("Организации", data.organizations)}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const BusinessSettingsPage = ({ embedded = false }) => {
    const [viewModel, setViewModel] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingSectionKey, setSavingSectionKey] = useState("");
    const [error, setError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState("");
    const [manualRunState, setManualRunState] = useState({ job: "", jobType: "", type: "", message: "" });
    const [competitorImportStatus, setCompetitorImportStatus] = useState(null);
    const [competitorImportState, setCompetitorImportState] = useState({ running: false, type: "", message: "" });
    const [openSections, setOpenSections] = useState(buildInitialOpenSections);
    const [activeInnerTab, setActiveInnerTab] = useState(innerTabs[0].key);

    useEffect(() => {
        const loadView = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await getBusinessSettingsView();
                setViewModel(data);
            } catch (loadError) {
                console.error("Error loading business settings view:", loadError);
                setError("Не удалось загрузить общие настройки.");
            } finally {
                setLoading(false);
            }
        };

        loadView();
    }, []);

    const loadCompetitorImportStatus = async () => {
        try {
            const result = await getCompetitorPriceImportStatus();
            setCompetitorImportStatus(result);
            return result;
        } catch (loadError) {
            setCompetitorImportState((current) => ({
                ...current,
                type: "error",
                message: "Не удалось загрузить статус цен конкурентов.",
            }));
            return null;
        }
    };

    useEffect(() => {
        loadCompetitorImportStatus();
    }, []);

    useEffect(() => {
        if (!competitorImportState.running) return undefined;
        const timer = window.setInterval(async () => {
            const result = await loadCompetitorImportStatus();
            if (result && !result.is_running && result.last_run?.status !== "running") {
                setCompetitorImportState({ running: false, type: result.last_run?.status === "failed" ? "error" : "success", message: result.last_run?.status === "failed" ? "Импорт завершился ошибкой." : "Импорт завершён." });
            }
        }, 3000);
        return () => window.clearInterval(timer);
    }, [competitorImportState.running]);

    useEffect(() => {
        if (viewModel) {
            setDraft(buildDraftFromViewModel(viewModel));
        }
    }, [viewModel]);

    useEffect(() => {
        if (viewModel?.sections) {
            setOpenSections((current) => {
                const next = { ...buildInitialOpenSections(), ...current };
                viewModel.sections.forEach((section) => {
                    if (!(section.key in next)) {
                        next[section.key] = false;
                    }
                });
                return next;
            });
        }
    }, [viewModel]);

    const handleDraftChange = (key, value) => {
        setDraft((current) => ({
            ...(current || {}),
            [key]: value,
        }));
    };

    const handleCancel = () => {
        setSaveError("");
        setSaveSuccess("");
        setManualRunState({ job: "", jobType: "", type: "", message: "" });
        setDraft(buildDraftFromViewModel(viewModel));
    };

    const handleSave = async (sectionKey) => {
        setSavingSectionKey(sectionKey);
        setSaveError("");
        setSaveSuccess("");
        try {
            let updated;
            if (sectionKey === "master_catalog" || sectionKey === "stock_mapping_mode" || sectionKey === "orders_biotus") {
                updated = await updateBusinessSettingsControlPlaneScope(buildUpdatePayload(draft));
            } else if (sectionKey === "pricing") {
                updated = await updateBusinessSettingsPricingScope(buildPricingUpdatePayload(draft));
            } else {
                throw new Error("Неподдерживаемая секция сохранения.");
            }

            setViewModel(updated);
            setSaveSuccess("Настройки сохранены.");
        } catch (saveRequestError) {
            console.error("Error saving business settings:", saveRequestError);
            setSaveError(saveRequestError?.response?.data?.detail || saveRequestError?.message || "Не удалось сохранить общие настройки.");
        } finally {
            setSavingSectionKey("");
        }
    };

    const handleManualMasterRun = async (job) => {
        setManualRunState({ job, jobType: job, type: "", message: "" });
        try {
            const result = await runMasterCatalogJob(job);
            setManualRunState({
                job: "",
                jobType: job,
                type: "success",
                message: `Запуск отправлен. PID: ${result.pid}`,
            });
        } catch (runError) {
            const statusCode = runError?.response?.status;
            const detail = runError?.response?.data?.detail;
            setManualRunState({
                job: "",
                jobType: job,
                type: "error",
                message: statusCode === 409
                    ? "Master catalog уже выполняется."
                    : detail || runError?.message || "Не удалось запустить Master catalog.",
            });
        }
    };

    const handleCompetitorImport = async () => {
        setCompetitorImportState({ running: true, type: "", message: "Запуск импорта..." });
        try {
            await runCompetitorPriceImport();
            await loadCompetitorImportStatus();
            setCompetitorImportState({ running: true, type: "success", message: "Импорт запущен." });
        } catch (runError) {
            const detail = runError?.response?.data?.detail;
            setCompetitorImportState({
                running: false,
                type: "error",
                message: runError?.response?.status === 409 ? "Импорт уже выполняется." : detail || "Не удалось запустить импорт цен конкурентов.",
            });
            await loadCompetitorImportStatus();
        }
    };

    const handleToggleSection = (sectionKey) => {
        setOpenSections((current) => ({
            ...current,
            [sectionKey]: !current[sectionKey],
        }));
        setSaveError("");
        setSaveSuccess("");
        setManualRunState({ job: "", jobType: "", type: "", message: "" });
        setDraft(buildDraftFromViewModel(viewModel));
    };

    return (
        <div style={embedded ? embeddedPageStyle : pageStyle}>
            {!embedded && (
                <div className="og-card" style={{ padding: "20px 24px", display: "grid", gap: "10px" }}>
                    <h1 style={{ margin: 0, fontSize: "28px", color: "#111827" }}>Общие настройки</h1>
                    <p className="og-mini">
                        Control-plane страница для scheduler, pricing и общих business pipeline настроек.
                    </p>
                </div>
            )}

            {loading && (
                <div className="og-card" style={{ padding: "20px 24px" }}>
                    <p className="og-mini">Загрузка общих настроек...</p>
                </div>
            )}

            {!loading && error && (
                <div className="og-callout danger">
                    <span className="ic">⚠</span>
                    <div>{error}</div>
                </div>
            )}

            {!loading && !error && viewModel && (
                <>
                    {saveError && (
                        <div className="og-callout danger">
                            <span className="ic">⚠</span>
                            <div>{saveError}</div>
                        </div>
                    )}

                    {saveSuccess && (
                        <div className="og-callout good">
                            <span className="ic">✓</span>
                            <div>{saveSuccess}</div>
                        </div>
                    )}

                    <div className="og-tabbar">
                        {innerTabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                className={activeInnerTab === tab.key ? "og-tab active" : "og-tab"}
                                onClick={() => setActiveInnerTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {sortSections(viewModel.sections)
                        .filter((section) => visibleSectionKeys.has(section.key) && section.key === activeInnerTab)
                        .map((section) => (
                        <SectionCard
                            key={section.key}
                            section={section}
                            isOpen={Boolean(openSections[section.key])}
                            saving={savingSectionKey === section.key}
                            onToggle={handleToggleSection}
                            onCancel={handleCancel}
                            onSave={handleSave}
                            draft={draft}
                            onDraftChange={handleDraftChange}
                            manualRunState={manualRunState}
                            onManualRun={handleManualMasterRun}
                            competitorImportStatus={competitorImportStatus}
                            competitorImportState={competitorImportState}
                            onCompetitorImport={handleCompetitorImport}
                        />
                    ))}
                    {activeInnerTab === "pricing_scopes" && <PricingScopesCard />}
                </>
            )}
        </div>
    );
};

export default BusinessSettingsPage;
